import { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { useServiceWorker } from '@/utils/serviceWorker'
import { Button } from './Button'

interface OfflineIndicatorProps {
  className?: string
}

export function OfflineIndicator({ className = '' }: OfflineIndicatorProps) {
  const { isOnline, isUpdateAvailable, updateServiceWorker, resetUpdateState, clearCache } = useServiceWorker()
  const [showDetails, setShowDetails] = useState(false)
  const [retryCount, setRetryCount] = useState(0)
  const [showUpdateBanner, setShowUpdateBanner] = useState(true)

  // Auto-retry connection check
  useEffect(() => {
    if (!isOnline && retryCount < 3) {
      const timer = setTimeout(() => {
        setRetryCount(prev => prev + 1)
        // Trigger a network check
        window.dispatchEvent(new Event('online'))
      }, 5000 * (retryCount + 1)) // Exponential backoff

      return () => clearTimeout(timer)
    }
  }, [isOnline, retryCount])

  // Reset retry count when back online
  useEffect(() => {
    if (isOnline) {
      setRetryCount(0)
    }
  }, [isOnline])

  // Reset update banner when a new update becomes available
  useEffect(() => {
    if (isUpdateAvailable) {
      setShowUpdateBanner(true)
    }
  }, [isUpdateAvailable])

  const handleUpdate = () => {
    updateServiceWorker()
    // Dismiss the banner after triggering update
    setShowUpdateBanner(false)
    // Note: updateServiceWorker already resets the state internally
  }

  const handleLater = () => {
    // Dismiss the update banner and reset the update state
    setShowUpdateBanner(false)
    resetUpdateState()
    console.log('Update dismissed for later')
  }

  const handleRetry = () => {
    window.location.reload()
  }

  const handleClearCache = async () => {
    await clearCache()
    window.location.reload()
  }

  return (
    <>
      {/* Offline Indicator */}
      <AnimatePresence>
        {!isOnline && (
          <motion.div
            initial={{ y: -100, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: -100, opacity: 0 }}
            transition={{ type: 'spring', damping: 25, stiffness: 500 }}
            className={`fixed top-0 left-0 right-0 z-50 bg-warning text-warning-foreground px-4 py-3 shadow-lg ${className}`}
          >
            <div className="max-w-7xl mx-auto flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="flex-shrink-0">
                  <div className="w-4 h-4 bg-current rounded-full animate-pulse" />
                </div>
                <div className="flex-1">
                  <p className="text-sm font-medium">
                    No internet connection
                  </p>
                  <p className="text-xs opacity-90">
                    You can still use the app offline
                  </p>
                </div>
              </div>
              
              <div className="flex items-center space-x-2">
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => setShowDetails(!showDetails)}
                  className="text-xs"
                >
                  {showDetails ? 'Hide' : 'Details'}
                </Button>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={handleRetry}
                  className="text-xs"
                >
                  Retry
                </Button>
              </div>
            </div>

            {/* Detailed offline info */}
            <AnimatePresence>
              {showDetails && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.2 }}
                  className="mt-3 pt-3 border-t border-current/20"
                >
                  <div className="max-w-7xl mx-auto">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-xs">
                      <div>
                        <p className="font-medium mb-1">Available offline:</p>
                        <ul className="space-y-1 opacity-90">
                          <li>• View cached challenges</li>
                          <li>• Track progress</li>
                          <li>• Read instructions</li>
                        </ul>
                      </div>
                      <div>
                        <p className="font-medium mb-1">Limited features:</p>
                        <ul className="space-y-1 opacity-90">
                          <li>• No new challenges</li>
                          <li>• No sharing</li>
                          <li>• No data sync</li>
                        </ul>
                      </div>
                      <div>
                        <p className="font-medium mb-1">Troubleshooting:</p>
                        <div className="space-y-2">
                          <Button
                            variant="secondary"
                            size="sm"
                            onClick={handleClearCache}
                            className="w-full text-xs"
                          >
                            Clear Cache
                          </Button>
                          <p className="opacity-90">
                            Retry attempts: {retryCount}/3
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Update Available Indicator */}
      <AnimatePresence>
        {isUpdateAvailable && isOnline && showUpdateBanner && (
          <motion.div
            initial={{ y: -100, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: -100, opacity: 0 }}
            transition={{ type: 'spring', damping: 25, stiffness: 500 }}
            className={`fixed top-0 left-0 right-0 z-50 bg-blue-600 dark:bg-primary text-white dark:text-primary-foreground px-4 py-3 shadow-lg ${className}`}
          >
            <div className="max-w-7xl mx-auto flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="flex-shrink-0">
                  <div className="w-4 h-4 bg-current rounded-full">
                    <div className="w-full h-full rounded-full border-2 border-current border-t-transparent animate-spin" />
                  </div>
                </div>
                <div className="flex-1">
                  <p className="text-sm font-medium">
                    Update available
                  </p>
                  <p className="text-xs opacity-90">
                    New features and improvements ready
                  </p>
                </div>
              </div>
              
              <div className="flex items-center space-x-2">
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={handleLater}
                  className="text-xs bg-white/20 hover:bg-white/30 text-white border-white/30"
                >
                  Later
                </Button>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={handleUpdate}
                  className="text-xs font-medium bg-white text-blue-600 hover:bg-gray-100 border-white"
                >
                  Update Now
                </Button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  )
}

export default OfflineIndicator